import type { ReactNode } from 'react';
import type { TimelineEntry } from '@fewerbags/schema';
import { EQUIPMENT_LABEL } from '../../lib/format';
import { formatClockRange, formatDuration } from '../../lib/planDates';
import { planCardStyle, planColor } from '../../lib/planColors';

function equipmentLabel(equipment: TimelineEntry['equipment']): string | null {
  if (!equipment) return null;
  const name = EQUIPMENT_LABEL[equipment.kind] ?? equipment.kind;
  const count = equipment.count > 1 ? ` ×${equipment.count}` : '';
  const temp = equipment.ovenTempF ? ` ${equipment.ovenTempF}°F` : '';
  const mode = equipment.ovenMode ? ` ${equipment.ovenMode}` : '';
  return `${name}${count}${temp}${mode}`;
}

/** One merged step: "17:10–17:25 · Turkey · Baste …" on a card tinted with the recipe's colour. */
export function TimelineStepCard({
  entry,
  recipeTitle,
  size = 'normal',
  done = false,
  children,
}: {
  entry: TimelineEntry;
  recipeTitle: string;
  /** "large" is cooking mode — readable from across the kitchen. */
  size?: 'normal' | 'large';
  done?: boolean;
  /** Timer controls, when the caller has any. */
  children?: ReactNode;
}) {
  const color = planColor(entry.colorIndex);
  const equipment = equipmentLabel(entry.equipment);
  const large = size === 'large';
  return (
    <li
      className={`rounded-xl border p-3 ${done ? 'opacity-60' : ''}`}
      style={planCardStyle(entry.colorIndex)}
    >
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
        <span
          className={`font-semibold tabular-nums ${large ? 'text-lg' : 'text-sm'}`}
          style={{ color: color.ink }}
        >
          {formatClockRange(entry.start, entry.end)}
        </span>
        <span className={large ? 'text-lg font-semibold' : 'text-sm font-semibold'}>
          {recipeTitle}
        </span>
      </div>
      <p className={`mt-1 ${large ? 'text-2xl leading-snug' : 'text-base leading-snug'}`}>
        {entry.text}
      </p>
      <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
        {entry.durationMin > 0 ? (
          <span className="rounded-full border px-2 py-0.5" style={{ borderColor: color.border }}>
            {entry.timer ? '⏲ ' : ''}
            {formatDuration(entry.durationMin)}
            {entry.attended ? ' hands-on' : ' unattended'}
          </span>
        ) : null}
        {equipment ? (
          <span className="rounded-full border px-2 py-0.5" style={{ borderColor: color.border }}>
            {equipment}
          </span>
        ) : null}
        {entry.doAhead ? (
          <span className="rounded-full border px-2 py-0.5" style={{ borderColor: color.border }}>
            can do ahead
          </span>
        ) : null}
        {entry.ingredients.length > 0 ? (
          <span className="opacity-80">{entry.ingredients.join(', ')}</span>
        ) : null}
      </p>
      {children ? <div className="mt-3">{children}</div> : null}
    </li>
  );
}
